import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { ArrowDown, ArrowUp, FileText, GripVertical, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFormList } from '../hooks/useFormBuilder';
import {
  useCreateTaskType,
  useSetTaskForms,
  useTaskForms,
  useUpdateTaskType,
} from '../hooks/useJobManagement';

interface SelectedForm {
  formId: number;
  formName: string;
}

export interface EditTaskTypeDialogProps {
  mode: 'closed' | 'create' | 'edit';
  editingId: number | null;
  initialName: string;
  onClose: () => void;
}

export function EditTaskTypeDialog({
  mode,
  editingId,
  initialName,
  onClose,
}: EditTaskTypeDialogProps) {
  const createMutation = useCreateTaskType();
  const updateMutation = useUpdateTaskType();
  const setTaskFormsMutation = useSetTaskForms();

  const [name, setName] = useState('');
  const [selectedForms, setSelectedForms] = useState<SelectedForm[]>([]);
  const [formSearch, setFormSearch] = useState('');

  const { data: formListData } = useFormList();
  const { data: taskFormsData, isLoading: taskFormsLoading } = useTaskForms(editingId);

  const allForms = formListData?.values ?? [];

  // Reset form state whenever the dialog opens or switches between modes
  useEffect(() => {
    if (mode === 'closed') return;
    setName(mode === 'edit' ? initialName : '');
    setSelectedForms([]);
    setFormSearch('');
  }, [mode, editingId]);

  // Populate forms from the server once the query resolves
  useEffect(() => {
    if (editingId && taskFormsData?.data) {
      setSelectedForms(
        taskFormsData.data.map((tf) => ({
          formId: tf.formId,
          formName: tf.formName ?? `Form #${tf.formId}`,
        })),
      );
    }
  }, [editingId, taskFormsData]);

  const availableForms = useMemo(() => {
    const selectedIds = new Set(selectedForms.map((f) => f.formId));
    let pool = allForms.filter((f: any) => !selectedIds.has(f.id));
    if (formSearch) {
      const q = formSearch.toLowerCase();
      pool = pool.filter((f: any) => f.name?.toLowerCase().includes(q));
    }
    return pool;
  }, [allForms, selectedForms, formSearch]);

  function addForm(formId: number, formName: string) {
    if (selectedForms.some((f) => f.formId === formId)) return;
    setSelectedForms((prev) => [...prev, { formId, formName }]);
  }

  function removeForm(formId: number) {
    setSelectedForms((prev) => prev.filter((f) => f.formId !== formId));
  }

  function moveForm(index: number, direction: -1 | 1) {
    setSelectedForms((prev) => {
      const list = [...prev];
      const target = index + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return list;
    });
  }

  async function handleSave() {
    let taskTypeId = editingId;

    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, body: { name } });
    } else {
      const result = await createMutation.mutateAsync({ name });
      taskTypeId = result.id;
    }

    if (taskTypeId) {
      await setTaskFormsMutation.mutateAsync({
        taskTypeId,
        forms: selectedForms.map((f, idx) => ({
          formId: f.formId,
          sequence: idx + 1,
        })),
      });
    }

    onClose();
  }

  const isSaving =
    createMutation.isPending || updateMutation.isPending || setTaskFormsMutation.isPending;

  return (
    <Dialog open={mode !== 'closed'} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Task Type' : 'Add Task Type'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="tt-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="tt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter task type name"
            />
          </div>

          {/* Attached Forms (ordered) */}
          <div className="space-y-2">
            <Label>
              Attached Forms <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Forms are presented to the engineer in the order shown below.
            </p>

            {/* Ordered list */}
            {selectedForms.length > 0 && (
              <div className="border rounded-lg divide-y">
                {selectedForms.map((sf, idx) => (
                  <div key={sf.formId} className="flex items-center gap-2 px-3 py-2">
                    <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs font-medium text-muted-foreground w-5">
                      {idx + 1}.
                    </span>
                    <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="flex-1 text-sm text-foreground">{sf.formName}</span>
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => moveForm(idx, -1)}
                        disabled={idx === 0 || taskFormsLoading}
                        className="p-1 rounded hover:bg-muted disabled:opacity-30"
                        title="Move up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveForm(idx, 1)}
                        disabled={idx === selectedForms.length - 1 || taskFormsLoading}
                        className="p-1 rounded hover:bg-muted disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeForm(sf.formId)}
                        disabled={taskFormsLoading}
                        className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-destructive"
                        title="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add form */}
            <div className="border rounded-lg overflow-hidden">
              <div className="p-2 border-b bg-muted/30">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search and add forms..."
                    value={formSearch}
                    onChange={(e) => setFormSearch(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
              </div>
              <div className="max-h-[150px] overflow-y-auto">
                {availableForms.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground text-center">
                    {selectedForms.length > 0 && !formSearch
                      ? 'All forms have been added'
                      : formSearch
                        ? 'No forms match your search'
                        : 'No forms available'}
                  </div>
                ) : (
                  availableForms.map((f: any) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => addForm(f.id, f.name)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left text-sm"
                      disabled={taskFormsLoading}
                    >
                      <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-foreground">{f.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {!taskFormsLoading && selectedForms.length === 0 && (
              <p className="text-xs text-red-500">At least one form must be attached</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || selectedForms.length === 0 || isSaving || taskFormsLoading}
          >
            {isSaving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
